import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@structural-climbing/ui'
import { useGymTimezone } from '../../contexts/GymContext'
import { gymLocalDate } from '../../lib/gymLocalDate'
import { formatDay } from '../members/renewalShift'

/** Fields that hold an instant, shown as the gym's calendar day. */
const DAY_FIELDS = new Set(['starts_at', 'expires_at', 'frozen_from', 'frozen_until'])

/** Fields never shown: ids the reader cannot use. */
const HIDDEN_FIELDS = new Set(['subscription_id', 'order_type'])

const isEmpty = (v: unknown) => v === null || v === undefined || v === ''

/** Turns a logged field value into what a person reads: days, plan names, statuses. */
export function useActivityFormat(planNames: Record<string, string>) {
  const { t, i18n } = useTranslation()
  const timezone = useGymTimezone()
  return (field: string, value: unknown): string => {
    if (isEmpty(value)) return t('activity.none')
    if (typeof value === 'boolean') return value ? t('activity.yes') : t('activity.no')
    if (DAY_FIELDS.has(field) && typeof value === 'string') {
      return formatDay(gymLocalDate(new Date(value), timezone), i18n.language)
    }
    if (field === 'gym_plan_id') return planNames[String(value)] ?? t('activity.unknownPlan')
    if (field === 'status') return t(`members.subStatus_${String(value)}`)
    if (field === 'payment_method') return t(`assignPlan.${String(value)}`)
    return String(value)
  }
}

export function visibleChanges(changes: Record<string, [unknown, unknown]>) {
  return Object.entries(changes).filter(([field]) => !HIDDEN_FIELDS.has(field))
}

/**
 * Every changed field side by side: the old value struck through, the new one
 * in bold. A field that had no value before shows a dash; one that was cleared
 * shows "none".
 */
export function ActivityDiffTable({
  changes,
  planNames,
}: {
  changes: Record<string, [unknown, unknown]>
  planNames: Record<string, string>
}) {
  const { t } = useTranslation()
  const format = useActivityFormat(planNames)
  const rows = visibleChanges(changes)
  if (!rows.length) return null

  return (
    <Table
      aria-label={t('activity.colDetails')}
      removeWrapper
      classNames={{
        base: 'overflow-hidden rounded-xl border border-divider',
        th: 'bg-default-50 text-xs',
        td: 'py-2 text-sm',
      }}
    >
      <TableHeader>
        <TableColumn>{t('activity.colField')}</TableColumn>
        <TableColumn>{t('activity.colBefore')}</TableColumn>
        <TableColumn>{t('activity.colAfter')}</TableColumn>
      </TableHeader>
      <TableBody>
        {rows.map(([field, [before, after]]) => (
          <TableRow key={field}>
            <TableCell className="text-foreground/55">{t(`activity.field_${field}`)}</TableCell>
            <TableCell className="text-foreground/50">
              {isEmpty(before) ? '—' : <s>{format(field, before)}</s>}
            </TableCell>
            <TableCell className="font-semibold">{format(field, after)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
